import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, MapPinIcon, MonitorIcon, ShieldCheckIcon, TrophyIcon } from 'lucide-react';
import { UniversityListing } from '../types/listings';
interface UniversityListingCardProps {
  listing: UniversityListing;
}

/**
 * A single advertiser listing on a guide page, built to read as a comparison
 * result rather than a display ad.
 *
 * Attention order is deliberate: school name, then the value-proposition
 * headline, then the three stats, then the CTA. Accreditation and location are
 * present for trust but deliberately quiet so they never outweigh the reason to
 * click.
 *
 * On narrow screens the logo and qualifier badge move to a strip across the top
 * so the school is identifiable before any scrolling, and the CTA spans the full
 * width with a tap-down state. The desktop two-column layout is unchanged.
 */
export function UniversityListingCard({
  listing
}: UniversityListingCardProps) {
  const {
    rank,
    schoolName,
    location,
    accreditation,
    topBadge,
    headline,
    benefits,
    logo,
    ctaText,
    ctaUrl,
    ctaSubtext,
    featured
  } = listing;
  const isInternal = ctaUrl.startsWith('/');
  const ctaClasses = 'inline-flex items-center justify-center text-center w-full min-h-[56px] lg:min-h-[60px] px-5 py-4 text-base font-semibold rounded-full text-white bg-primary shadow-ds-md hover:bg-primary-hover hover:shadow-ds-lg lg:hover:-translate-y-0.5 active:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-[background-color,box-shadow,transform] duration-150 ease-out';
  return <article className="relative" data-id="element-912" data-mp="3ojam2occg0v4">
      {featured && <div className="rounded-t-xl px-4 py-2.5 sm:py-3 flex items-center justify-center gap-2 bg-navy" data-id="element-913" data-mp="3oir7nhlwjulu">
          <TrophyIcon className="w-4 h-4 flex-shrink-0 text-gold-light" aria-hidden="true" data-id="element-914" data-mp="1q5hd3mbr4znk" />
          <span className="text-white text-xs sm:text-sm font-bold tracking-wide" data-id="element-915" data-mp="2xg5q2k8mr22m">
            #1 Recommended School
          </span>
        </div>}

      <div className={`bg-white transition-shadow duration-200 ease-out ${featured ? 'rounded-b-xl border-2 border-t-0 border-navy/20 shadow-lg hover:shadow-xl' : 'rounded-xl border border-gray-200 shadow-sm hover:shadow-md'}`} data-id="element-916" data-mp="2sidvfiglp1uq">
        {/* Mobile identity block — rank and qualifier badge, then the logo at
            full width. The logo is what makes a school recognizable, so it
            leads; the desktop layout keeps both in the right-hand column. */}
        <div className="lg:hidden px-4 sm:px-6 pt-4 pb-3 border-b border-gray-100" data-id="element-917" data-mp="3q6xgt26xeyik">
          <div className="flex items-center justify-between gap-3 min-h-[28px]" data-id="element-918" data-mp="3l69q6hmm0jov">
            <span className={`inline-flex items-center justify-center flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${featured ? 'bg-navy text-white' : 'bg-light border border-gray-200 text-navy'}`} data-id="element-919" data-mp="30wrkzce3ie0">
              #{rank}
              <span className="sr-only" data-id="element-920" data-mp="mjubaszp4eyf"> ranked school</span>
            </span>
            {topBadge && <span className="flex-shrink-0 inline-flex items-center text-right bg-light border border-gray-200 text-navy text-[11px] font-semibold leading-tight px-2.5 py-1 rounded-full" data-id="element-921" data-mp="8oi5nqk0542q">
                {topBadge}
              </span>}
          </div>

          <div className="mt-3 h-16 flex items-center justify-center" data-id="element-922" data-mp="3nue93rv2l7z">
            <img src={logo} alt="" aria-hidden="true" className="max-h-full max-w-[200px] object-contain" loading="lazy" data-id="element-923" data-mp="3l4c0zr5cgsrd" />
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-7 flex flex-col lg:flex-row lg:gap-8" data-id="element-924" data-mp="3c3e5xycvvvkx">
          {/* Main content */}
          <div className="flex-1 min-w-0 flex gap-3 sm:gap-4" data-id="element-925" data-mp="3h160l04wxvst">
            {/* Rank appears in the mobile block above, so it is desktop-only here. */}
            <div className="hidden lg:block flex-shrink-0 pt-0.5" data-id="element-926" data-mp="341wq6dyyx5yr">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${featured ? 'bg-navy text-white' : 'border-2 border-gray-200 text-gray-500'}`} data-id="element-927" data-mp="2zbgdiw2z1odg">
                <span className="text-sm font-bold" data-id="element-928" data-mp="2qc2zdq3wkbme">{rank}</span>
                <span className="sr-only" data-id="element-929" data-mp="2lanf409ju952">Ranked number {rank}</span>
              </div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-930" data-mp="2gddj21rnmz89">
              {/* Identity */}
              <h3 className="text-lg sm:text-2xl font-bold text-navy leading-tight" data-id="element-931" data-mp="2cbimfako5a0t">
                {schoolName}
              </h3>

              <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-gray-500" data-id="element-932" data-mp="ekpdhlrjfzpb">
                <span className="inline-flex items-center gap-1.5" data-id="element-933" data-mp="aht1wf0f13uv">
                  <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" data-id="element-934" data-mp="1sgqyqjkdwzku" />
                  {location}
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-935" data-mp="227lohxwuahb1">
                  <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-936" data-mp="268x3l9i37k4z" />
                  {accreditation}
                </span>
              </div>

              {/* The reason to click */}
              <p className="mt-3 sm:mt-4 text-base sm:text-lg font-bold text-crimson leading-snug" data-id="element-937" data-mp="1ijcmkw5huwyc">
                {headline}
              </p>

              {/* Benefits */}
              <ul className="mt-3 sm:mt-4 space-y-2 sm:space-y-2.5" data-id="element-938" data-mp="1nft9xo4vd7ad">
                {benefits.map(benefit => <li key={benefit} className="flex items-start gap-2.5" data-id="element-939" data-mp="2yx2rg54ftp91">
                    <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-940" data-mp="2yz5q1djnoqpd" />
                    <span className="text-[13px] sm:text-sm text-gray-700 leading-relaxed" data-id="element-941" data-mp="usydf586sv8m">
                      {benefit}
                    </span>
                  </li>)}
              </ul>

            </div>
          </div>

          {/* Logo + CTA. On mobile only the CTA and its reassurance line show
              here; the logo and badge already appeared in the strip above. */}
          <div className="mt-5 pt-5 border-t border-gray-100 lg:mt-0 lg:pt-0 lg:border-t-0 lg:border-l lg:pl-8 lg:w-60 lg:flex-shrink-0 flex flex-col items-center justify-center gap-3 lg:gap-4" data-id="element-942" data-mp="b73i99kvwbjb">
            {topBadge && <span className="hidden lg:inline-flex items-center justify-center text-center bg-light border border-gray-200 text-navy text-xs font-semibold leading-tight px-3 py-1.5 rounded-full" data-id="element-943" data-mp="l1veoa2qcsci">
                {topBadge}
              </span>}

            <div className="hidden lg:flex w-full h-20 items-center justify-center" data-id="element-944" data-mp="241kx9ew76bfz">
              <img src={logo} alt={`${schoolName} logo`} className="max-w-[200px] max-h-full object-contain" loading="lazy" data-id="element-945" data-mp="28u4kz4olmmyi" />
            </div>

            {isInternal ? <Link to={ctaUrl} className={ctaClasses} data-id="element-946" data-mp="1z0x52pw8fok6">
                {ctaText}
              </Link> : <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className={ctaClasses} data-id="element-947" data-mp="2rroy5ei03x3a">
                {ctaText}
              </a>}

            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-600" data-id="element-948" data-mp="2ipxusgaxo2cl">
              <MonitorIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-949" data-mp="12bzap8se9snj" />
              {ctaSubtext}
            </p>
          </div>
        </div>
      </div>
    </article>;
}